import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { throwError as observableThrowError, Observable, of } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { PosDevice } from "../../models/entity/posDevice";
import { SelectedItemIdDto } from "../../models/dto/selectedItemIdDto";
import { RequestCacheService } from "../core/request-cache.service";
import { StoreView } from "../../models/dto/storeView";
import { SmsSettingEntity } from "../../models/entity/sms/sms-setting-entity";
import { ReceiptSetting } from "../../models/entity/receiptSetting";
import { BaseRestService } from "../base-rest.service";
import { SmsTemplateEntity } from "../../models/entity/sms/sms-template-entity";
import { ServiceCar } from "../../models/entity/serviceCar";
import { StoreTemplate } from "../../models/entity/store/store-template";
import { StoreHangerTypeView } from "../../models/dto/store/store-hanger-type-view.model";
import { MailSettingModel } from "../../models/entity/store/mail-setting.model";
import { StockTrackingEntity } from "../../models/entity/order/stock-tracking.entity";
import { BillingSettingModel } from "../../models/entity/billing/billing-setting.model";
import { NotificationPreferenceEntity } from "../../models/entity/notification/notification-preference-entity";
import { PushNotificationTemplateEntity } from "../../models/entity/notification/push-notification-template.entity";
import { SegmentValueEntity } from "../../models/entity/segment/segment-value.entity";
import { CashRegisterEntity } from "../../models/entity/cash-register/cash-register-entity";
import { PayrollSettingEntity } from "../../models/entity/payroll/payroll-setting.entity";
import { PaymentType } from "../../models/entity/paymentType";
import { Device } from "../../models/entity/device";
import { SessionStorageService } from "../session-storage.service";
import { OrderItemType } from "../../models/entity/product/orderItemType";
import { StoreSettingEntity } from "../../models/entity/store/store-setting.entity";
import { Address } from "../../models/entity/address";
import { StorePaymentTypeOptionsResponse } from "../../models/rest/payment/store-payment-type-options.response";

@Injectable({
  providedIn: 'root'
})
export class StoreCacheService extends BaseRestService {
  constructor(protected httpClient: HttpClient, protected sessionStorageService: SessionStorageService,
    private cache: RequestCacheService) {
    super(httpClient, sessionStorageService);
  }

  private getCachedResponse<T>(url: string): Observable<T> {
    const cachedResponse = this.cache.get(url);
    if (cachedResponse) {
      return of(cachedResponse);
    }
    return null!;
  }

  private fetchAndCache<T>(url: string): Observable<T> {
    return this.httpGet<T>(url).pipe(
      tap((response) => {
        this.cache.put(url, response);
      }),
      catchError(this.errorHandler)
    );
  }

  getStores(): Observable<StoreView[]> {
    const url = `/stores`;
    const cachedResponse = this.getCachedResponse<StoreView[]>(url);
    return cachedResponse ? cachedResponse : this.fetchAndCache<StoreView[]>(url);
  }

  getStoreById(id: number): Observable<any> {
    const url = `/stores/${id}`;
    const cachedResponse = this.getCachedResponse<any>(url);
    return cachedResponse ? cachedResponse : this.fetchAndCache<any>(url);
  }

  getStoreSelectList(storeId: number, type: string): Observable<SelectedItemIdDto[]> {
    const url = `/stores/${storeId}/allowed?type=${type}`;
    const cachedResponse = this.getCachedResponse<SelectedItemIdDto[]>(url);
    return cachedResponse ? cachedResponse : this.fetchAndCache<SelectedItemIdDto[]>(url);
  }

  getAllowedStoreSelectList(storeId: number, type: string): Observable<SelectedItemIdDto[]> {
    const url = `/stores/${storeId}/store-relation?type=${type}`;
    const cachedResponse = this.getCachedResponse<SelectedItemIdDto[]>(url);
    return cachedResponse ? cachedResponse : this.fetchAndCache<SelectedItemIdDto[]>(url);
  }

  getStoreMainProcessTypes(storeId: number): Observable<OrderItemType[]> {
    const url = `/stores/${storeId}/main-process-types`;
    const cachedResponse = this.getCachedResponse<OrderItemType[]>(url);
    return cachedResponse ? cachedResponse : this.fetchAndCache<OrderItemType[]>(url);
  }

  getStoreMainProcessTypesForWeb(storeId: number): Observable<OrderItemType[]> {
    const url = `/stores/${storeId}/main-process-types-web`;
    const cachedResponse = this.getCachedResponse<OrderItemType[]>(url);
    return cachedResponse ? cachedResponse : this.fetchAndCache<OrderItemType[]>(url);
  }

  getPosDevices(storeId: number): Observable<PosDevice[]> {
    const url = `/stores/${storeId}/pos-devices`;
    const cachedResponse = this.getCachedResponse<PosDevice[]>(url);
    return cachedResponse ? cachedResponse : this.fetchAndCache<PosDevice[]>(url);
  }

  getServiceCars(storeId: number): Observable<ServiceCar[]> {
    const url = `/stores/${storeId}/service-cars`;
    const cachedResponse = this.getCachedResponse<ServiceCar[]>(url);
    return cachedResponse ? cachedResponse : this.fetchAndCache<ServiceCar[]>(url);
  }

  getDevices(storeId: number): Observable<Device[]> {
    const url = `/stores/${storeId}/devices`;
    const cachedResponse = this.getCachedResponse<Device[]>(url);
    return cachedResponse ? cachedResponse : this.fetchAndCache<Device[]>(url);
  }

  getPaymentTypes(storeId: number): Observable<PaymentType[]> {
    const url = `/stores/${storeId}/payment-types`;
    const cachedResponse = this.getCachedResponse<PaymentType[]>(url);

    return cachedResponse ? cachedResponse : this.fetchAndCache<PaymentType[]>(url);
  }

  getPaymentTypesForCashRegister(storeId: number): Observable<PaymentType[]> {
    const url = `/stores/${storeId}/payment-types-cash-register`;
    const cachedResponse = this.getCachedResponse<PaymentType[]>(url);
    return cachedResponse ? cachedResponse : this.fetchAndCache<PaymentType[]>(url);
  }
  getPaymentTypesOptions(storeId: number): Observable<StorePaymentTypeOptionsResponse> {
    const url = `/stores/${storeId}/payment-types-options`;
    const cachedResponse = this.getCachedResponse<StorePaymentTypeOptionsResponse>(url);
    return cachedResponse ? cachedResponse : this.fetchAndCache<StorePaymentTypeOptionsResponse>(url);
  }

  getSetting(storeId: number): Observable<StoreSettingEntity> {
    const url = `/stores/${storeId}/setting`;
    const cachedResponse = this.getCachedResponse<StoreSettingEntity>(url);
    return cachedResponse ? cachedResponse : this.fetchAndCache<StoreSettingEntity>(url);
  }

  getPayrollSetting(storeId: number): Observable<PayrollSettingEntity> {
    const url = `/stores/${storeId}/payroll-setting`;
    const cachedResponse = this.getCachedResponse<PayrollSettingEntity>(url);
    return cachedResponse ? cachedResponse : this.fetchAndCache<PayrollSettingEntity>(url);
  }

  getSmsSetting(storeId: number, channelType: string): Observable<SmsSettingEntity> {
    const url = `/stores/${storeId}/sms-setting?channel-type=${channelType}`;
    const cachedResponse = this.getCachedResponse<SmsSettingEntity>(url);
    return cachedResponse ? cachedResponse : this.fetchAndCache<SmsSettingEntity>(url);
  }


  getMailSetting(storeId: number): Observable<MailSettingModel> {
    const url = `/stores/${storeId}/mail-setting`;
    const cachedResponse = this.getCachedResponse<MailSettingModel>(url);
    return cachedResponse ? cachedResponse : this.fetchAndCache<MailSettingModel>(url);
  }

  getReceiptSettingByReceiptType(storeId: number, receiptType: string): Observable<ReceiptSetting> {
    const url = `/stores/${storeId}/receipt-setting?type=${receiptType}`;
    const cachedResponse = this.getCachedResponse<ReceiptSetting>(url);
    return cachedResponse ? cachedResponse : this.fetchAndCache<ReceiptSetting>(url);
  }

  getLabelSetting(storeId: number): Observable<ReceiptSetting> {
    const url = `/stores/${storeId}/label-setting`;
    const cachedResponse = this.getCachedResponse<ReceiptSetting>(url);
    return cachedResponse ? cachedResponse : this.fetchAndCache<ReceiptSetting>(url);
  }

  getSmsTemplates(storeId: number): Observable<SmsTemplateEntity[]> {
    const url = `/stores/${storeId}/sms-templates`;
    const cachedResponse = this.getCachedResponse<SmsTemplateEntity[]>(url);
    return cachedResponse ? cachedResponse : this.fetchAndCache<SmsTemplateEntity[]>(url);
  }

  getPushNotificationTemplates(storeId: number): Observable<PushNotificationTemplateEntity[]> {
    const url = `/stores/${storeId}/push-notification-templates`;
    const cachedResponse = this.getCachedResponse<PushNotificationTemplateEntity[]>(url);
    return cachedResponse ? cachedResponse : this.fetchAndCache<PushNotificationTemplateEntity[]>(url);
  }

  getNotificationPreferences(storeId: number): Observable<NotificationPreferenceEntity[]> {
    const url = `/stores/${storeId}/notification-preferences`;
    const cachedResponse = this.getCachedResponse<NotificationPreferenceEntity[]>(url);
    return cachedResponse ? cachedResponse : this.fetchAndCache<NotificationPreferenceEntity[]>(url);
  }

  getStoreTemplates(storeId: number): Observable<StoreTemplate[]> {
    const url = `/stores/${storeId}/templates`;
    const cachedResponse = this.getCachedResponse<StoreTemplate[]>(url);
    return cachedResponse ? cachedResponse : this.fetchAndCache<StoreTemplate[]>(url);
  }

  getStoreHangerTypes(storeId: number): Observable<StoreHangerTypeView[]> {
    const url = `/stores/${storeId}/hanger-types`;
    const cachedResponse = this.getCachedResponse<StoreHangerTypeView[]>(url);
    return cachedResponse ? cachedResponse : this.fetchAndCache<StoreHangerTypeView[]>(url);
  }

  getStockTrackings(storeId: number): Observable<StockTrackingEntity[]> {
    const url = `/stores/${storeId}/stock-trackings`;
    const cachedResponse = this.getCachedResponse<StockTrackingEntity[]>(url);
    return cachedResponse ? cachedResponse : this.fetchAndCache<StockTrackingEntity[]>(url);
  }

  getBillingSetting(storeId: number): Observable<BillingSettingModel> {
    const url = `/stores/${storeId}/billing-setting`;
    const cachedResponse = this.getCachedResponse<BillingSettingModel>(url);
    return cachedResponse ? cachedResponse : this.fetchAndCache<BillingSettingModel>(url);
  }

  getSegmentValuesByStoreId(storeId: number, segmentId: number): Observable<SegmentValueEntity[]> {
    const url = `/stores/${storeId}/segment-values?segment-id=${segmentId}`;
    const cachedResponse = this.getCachedResponse<SegmentValueEntity[]>(url);
    return cachedResponse ? cachedResponse : this.fetchAndCache<SegmentValueEntity[]>(url);
  }

  getCashRegistersByStoreId(storeId: number): Observable<CashRegisterEntity[]> {
    const url = `/stores/${storeId}/cash-registers`;
    const cachedResponse = this.getCachedResponse<CashRegisterEntity[]>(url);
    return cachedResponse ? cachedResponse : this.fetchAndCache<CashRegisterEntity[]>(url);
  }

  getAddressByStoreId(storeId: number): Observable<Address> {
    const url = `/stores/${storeId}/address`;
    const cachedResponse = this.getCachedResponse<Address>(url);
    return cachedResponse ? cachedResponse : this.fetchAndCache<Address>(url);
  }

  // Cache temizleme metodları
  public clearStoresCache(): void {
    const url = `/stores`;
    this.cache.clearByKeyPattern(url);
  }

  public clearStoreByIdCache(storeId: number): void {
    const url = `/stores/${storeId}`;
    this.cache.clearByKeyPattern(url);
  }

  public clearStoreSelectListCache(storeId: number, type: string): void {
    const url = `/stores/${storeId}/allowed?type=${type}`;
    this.cache.clearByKeyPattern(url);
  }

  public clearAllowedStoreSelectListCache(storeId: number, type: string): void {
    const url = `/stores/${storeId}/store-relation?type=${type}`;
    this.cache.clearByKeyPattern(url);
  }

  public clearStoreMainProcessTypesCache(storeId: number): void {
    const url = `/stores/${storeId}/main-process-types`;
    this.cache.clearByKeyPattern(url);
  }

  public clearStoreMainProcessTypesForWebCache(storeId: number): void {
    const url = `/stores/${storeId}/main-process-types-web`;
    this.cache.clearByKeyPattern(url);
  }

  public clearPosDevicesCache(storeId: number): void {
    const url = `/stores/${storeId}/pos-devices`;
    this.cache.clearByKeyPattern(url);
  }

  public clearServiceCarsCache(storeId: number): void {
    const url = `/stores/${storeId}/service-cars`;
    this.cache.clearByKeyPattern(url);
  }

  public clearDevicesCache(storeId: number): void {
    const url = `/stores/${storeId}/devices`;
    this.cache.clearByKeyPattern(url);
  }

  public clearPaymentTypesCache(storeId: number): void {
    const url = `/stores/${storeId}/payment-types`;
    this.cache.clearByKeyPattern(url);
  }

  public clearPaymentTypesForCashRegisterCache(storeId: number): void {
    const url = `/stores/${storeId}/payment-types-cash-register`;
    this.cache.clearByKeyPattern(url);
  }

  public clearSettingCache(storeId: number): void {
    const url = `/stores/${storeId}/setting`;
    this.cache.clearByKeyPattern(url);
  }

  public clearPayrollSettingCache(storeId: number): void {
    const url = `/stores/${storeId}/payroll-setting`;
    this.cache.clearByKeyPattern(url);
  }

  public clearSmsSettingCache(storeId: number, channelType: string): void {
    const url = `/stores/${storeId}/sms-setting?channel-type=${channelType}`;
    this.cache.clearByKeyPattern(url);
  }

  public clearMailSettingCache(storeId: number): void {
    const url = `/stores/${storeId}/mail-setting`;
    this.cache.clearByKeyPattern(url);
  }

  public clearReceiptSettingCache(storeId: number, receiptType: string): void {
    const url = `/stores/${storeId}/receipt-setting?type=${receiptType}`;
    this.cache.clearByKeyPattern(url);
  }

  public clearLabelSettingCache(storeId: number): void {
    const url = `/stores/${storeId}/label-setting`;
    this.cache.clearByKeyPattern(url);
  }

  public clearSmsTemplatesCache(storeId: number): void {
    const url = `/stores/${storeId}/sms-templates`;
    this.cache.clearByKeyPattern(url);
  }

  public clearPushNotificationTemplatesCache(storeId: number): void {
    const url = `/stores/${storeId}/push-notification-templates`;
    this.cache.clearByKeyPattern(url);
  }

  public clearNotificationPreferencesCache(storeId: number): void {
    const url = `/stores/${storeId}/notification-preferences`;
    this.cache.clearByKeyPattern(url);
  }

  public clearStoreTemplatesCache(storeId: number): void {
    const url = `/stores/${storeId}/templates`;
    this.cache.clearByKeyPattern(url);
  }

  public clearStoreHangerTypesCache(storeId: number): void {
    const url = `/stores/${storeId}/hanger-types`;
    this.cache.clearByKeyPattern(url);
  }

  public clearStockTrackingsCache(storeId: number): void {
    const url = `/stores/${storeId}/stock-trackings`;
    this.cache.clearByKeyPattern(url);
  }

  public clearBillingSettingCache(storeId: number): void {
    const url = `/stores/${storeId}/billing-setting`;
    this.cache.clearByKeyPattern(url);
  }

  public clearSegmentValuesCache(storeId: number, segmentId: number): void {
    const url = `/stores/${storeId}/segment-values?segment-id=${segmentId}`;
    this.cache.clearByKeyPattern(url);
  }

  public clearCashRegistersByStoreIdCache(storeId: number): void {
    const url = `/stores/${storeId}/cash-registers`;
    this.cache.clearByKeyPattern(url);
  }

  public clearAddressByStoreIdCache(storeId: number): void {
    const url = `/stores/${storeId}/address`;
    this.cache.clearByKeyPattern(url);
  }

  // Genel cache temizleme metodları
  public clearAllStoreCache(storeId: number): void {
    // Belirli bir store için tüm cache'leri temizler
    this.clearStoreByIdCache(storeId);
    this.clearStoreSelectListCache(storeId, '');
    this.clearAllowedStoreSelectListCache(storeId, '');
    this.clearStoreMainProcessTypesCache(storeId);
    this.clearStoreMainProcessTypesForWebCache(storeId);
    this.clearPosDevicesCache(storeId);
    this.clearServiceCarsCache(storeId);
    this.clearDevicesCache(storeId);
    this.clearPaymentTypesCache(storeId);
    this.clearPaymentTypesForCashRegisterCache(storeId);
    this.clearSettingCache(storeId);
    this.clearPayrollSettingCache(storeId);
    this.clearSmsSettingCache(storeId, '');
    this.clearMailSettingCache(storeId);
    this.clearReceiptSettingCache(storeId, '');
    this.clearLabelSettingCache(storeId);
    this.clearSmsTemplatesCache(storeId);
    this.clearPushNotificationTemplatesCache(storeId);
    this.clearNotificationPreferencesCache(storeId);
    this.clearStoreTemplatesCache(storeId);
    this.clearStoreHangerTypesCache(storeId);
    this.clearStockTrackingsCache(storeId);
    this.clearBillingSettingCache(storeId);
    this.clearSegmentValuesCache(storeId, 0);
    this.clearCashRegistersByStoreIdCache(storeId);
    this.clearAddressByStoreIdCache(storeId);
  }

  public clearAllStoresCache(): void {
    // Tüm stores cache'ini temizler
    this.clearStoresCache();
  }

  // Parametresiz genel cache temizleme metodları
  public clearAllServiceCarsCache(): void {
    // Tüm store'lar için service-cars cache'ini temizler
    const urlPattern = `/stores/*/service-cars`;
    this.cache.clearByKeyPattern(urlPattern);
  }

  public clearAllPosDevicesCache(): void {
    // Tüm store'lar için pos-devices cache'ini temizler
    const urlPattern = `/stores/*/pos-devices`;
    this.cache.clearByKeyPattern(urlPattern);
  }

  public clearAllDevicesCache(): void {
    // Tüm store'lar için devices cache'ini temizler
    const urlPattern = `/stores/*/devices`;
    this.cache.clearByKeyPattern(urlPattern);
  }

  public clearAllPaymentTypesCache(): void {
    // Tüm store'lar için payment-types cache'ini temizler
    const urlPattern = `/stores/*/payment-types`;
    this.cache.clearByKeyPattern(urlPattern);
  }

  public clearAllPaymentTypesForCashRegisterCache(): void {
    // Tüm store'lar için payment-types-cash-register cache'ini temizler
    const urlPattern = `/stores/*/payment-types-cash-register`;
    this.cache.clearByKeyPattern(urlPattern);
  }

  public clearAllSettingsCache(): void {
    // Tüm store'lar için settings cache'ini temizler
    const urlPattern = `/stores/*/setting`;
    this.cache.clearByKeyPattern(urlPattern);
  }

  public clearAllPayrollSettingsCache(): void {
    // Tüm store'lar için payroll-settings cache'ini temizler
    const urlPattern = `/stores/*/payroll-setting`;
    this.cache.clearByKeyPattern(urlPattern);
  }

  public clearAllSmsSettingsCache(): void {
    // Tüm store'lar için sms-settings cache'ini temizler
    const urlPattern = `/stores/*/sms-setting`;
    this.cache.clearByKeyPattern(urlPattern);
  }

  public clearAllMailSettingsCache(): void {
    // Tüm store'lar için mail-settings cache'ini temizler
    const urlPattern = `/stores/*/mail-setting`;
    this.cache.clearByKeyPattern(urlPattern);
  }

  public clearAllReceiptSettingsCache(): void {
    // Tüm store'lar için receipt-settings cache'ini temizler
    const urlPattern = `/stores/*/receipt-setting`;
    this.cache.clearByKeyPattern(urlPattern);
  }

  public clearAllLabelSettingsCache(): void {
    // Tüm store'lar için label-settings cache'ini temizler
    const urlPattern = `/stores/*/label-setting`;
    this.cache.clearByKeyPattern(urlPattern);
  }

  public clearAllSmsTemplatesCache(): void {
    // Tüm store'lar için sms-templates cache'ini temizler
    const urlPattern = `/stores/*/sms-templates`;
    this.cache.clearByKeyPattern(urlPattern);
  }

  public clearAllPushNotificationTemplatesCache(): void {
    // Tüm store'lar için push-notification-templates cache'ini temizler
    const urlPattern = `/stores/*/push-notification-templates`;
    this.cache.clearByKeyPattern(urlPattern);
  }

  public clearAllNotificationPreferencesCache(): void {
    // Tüm store'lar için notification-preferences cache'ini temizler
    const urlPattern = `/stores/*/notification-preferences`;
    this.cache.clearByKeyPattern(urlPattern);
  }

  public clearAllStoreTemplatesCache(): void {
    // Tüm store'lar için templates cache'ini temizler
    const urlPattern = `/stores/*/templates`;
    this.cache.clearByKeyPattern(urlPattern);
  }

  public clearAllStoreHangerTypesCache(): void {
    // Tüm store'lar için hanger-types cache'ini temizler
    const urlPattern = `/stores/*/hanger-types`;
    this.cache.clearByKeyPattern(urlPattern);
  }

  public clearAllStockTrackingsCache(): void {
    // Tüm store'lar için stock-trackings cache'ini temizler
    const urlPattern = `/stores/*/stock-trackings`;
    this.cache.clearByKeyPattern(urlPattern);
  }

  public clearAllBillingSettingsCache(): void {
    // Tüm store'lar için billing-settings cache'ini temizler
    const urlPattern = `/stores/*/billing-setting`;
    this.cache.clearByKeyPattern(urlPattern);
  }

  public clearAllSegmentValuesCache(): void {
    // Tüm store'lar için segment-values cache'ini temizler
    const urlPattern = `/stores/*/segment-values`;
    this.cache.clearByKeyPattern(urlPattern);
  }

  public clearAllCashRegistersCache(): void {
    // Tüm store'lar için cash-registers cache'ini temizler
    const urlPattern = `/stores/*/cash-registers`;
    this.cache.clearByKeyPattern(urlPattern);
  }

  public clearAllAddressesCache(): void {
    // Tüm store'lar için addresses cache'ini temizler
    const urlPattern = `/stores/*/address`;
    this.cache.clearByKeyPattern(urlPattern);
  }

  public clearAllStoreSelectListsCache(): void {
    // Tüm store'lar için allowed select lists cache'ini temizler
    const urlPattern = `/stores/*/allowed`;
    this.cache.clearByKeyPattern(urlPattern);
  }

  public clearAllAllowedStoreSelectListsCache(): void {
    // Tüm store'lar için store-relation select lists cache'ini temizler
    const urlPattern = `/stores/*/store-relation`;
    this.cache.clearByKeyPattern(urlPattern);
  }

  public clearAllMainProcessTypesCache(): void {
    // Tüm store'lar için main-process-types cache'ini temizler
    const urlPattern = `/stores/*/main-process-types`;
    this.cache.clearByKeyPattern(urlPattern);
  }

  public clearAllMainProcessTypesForWebCache(): void {
    // Tüm store'lar için main-process-types-web cache'ini temizler
    const urlPattern = `/stores/*/main-process-types-web`;
    this.cache.clearByKeyPattern(urlPattern);
  }

  // Kategorilere göre toplu temizleme metodları
  public clearAllDeviceRelatedCache(): void {
    // Tüm cihaz ile ilgili cache'leri temizler
    this.clearAllPosDevicesCache();
    this.clearAllDevicesCache();
    this.clearAllCashRegistersCache();
  }

  public clearAllPaymentRelatedCache(): void {
    // Tüm ödeme ile ilgili cache'leri temizler
    this.clearAllPaymentTypesCache();
    this.clearAllPaymentTypesForCashRegisterCache();
  }

  public clearAllSettingRelatedCache(): void {
    // Tüm ayar ile ilgili cache'leri temizler
    this.clearAllSettingsCache();
    this.clearAllPayrollSettingsCache();
    this.clearAllSmsSettingsCache();
    this.clearAllMailSettingsCache();
    this.clearAllReceiptSettingsCache();
    this.clearAllLabelSettingsCache();
    this.clearAllBillingSettingsCache();
  }

  public clearAllTemplateRelatedCache(): void {
    // Tüm şablon ile ilgili cache'leri temizler
    this.clearAllSmsTemplatesCache();
    this.clearAllPushNotificationTemplatesCache();
    this.clearAllStoreTemplatesCache();
  }

  public clearAllNotificationRelatedCache(): void {
    // Tüm bildirim ile ilgili cache'leri temizler
    this.clearAllNotificationPreferencesCache();
    this.clearAllPushNotificationTemplatesCache();
  }

  public clearAllStoreDataCache(): void {
    // Tüm store veri cache'lerini temizler
    this.clearAllStoresCache();
    this.clearAllStoreSelectListsCache();
    this.clearAllAllowedStoreSelectListsCache();
    this.clearAllMainProcessTypesCache();
    this.clearAllMainProcessTypesForWebCache();
    this.clearAllStoreHangerTypesCache();
    this.clearAllAddressesCache();
  }

  public clearAllCache(): void {
    // Tüm cache'leri temizler
    this.clearAllStoreDataCache();
    this.clearAllDeviceRelatedCache();
    this.clearAllPaymentRelatedCache();
    this.clearAllSettingRelatedCache();
    this.clearAllTemplateRelatedCache();
    this.clearAllNotificationRelatedCache();
    this.clearAllServiceCarsCache();
    this.clearAllStockTrackingsCache();
    this.clearAllSegmentValuesCache();
  }

  errorHandler(error: HttpErrorResponse) {
    return observableThrowError(error);
  }
}
